'use client';

import { useState } from 'react';
import styled from 'styled-components';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/Breadcrumbs';
import ZoomBox from '@/components/ZoomBox';
import FAQSection from '@/components/FAQSection';
import { COLORS } from '@/constants/colors';
import { measureFilterFaqs } from '@/data/faqData';

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${COLORS.white};
`;

const HeroSection = styled.section`
  width: 100%;
  padding: 80px 20px 40px;
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
  color: ${COLORS.white};
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 20px 30px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  color: ${COLORS.white};
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;

  @media (max-width: 768px) {
    padding: 30px 16px;
  }
`;

const VideoSection = styled.div`
  padding: 0;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const VideoTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  color: ${COLORS.darkGray};
  margin-bottom: 30px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const VideoCard = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.border};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  max-width: 900px;
  margin: 0 auto;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const InstructionsSection = styled.div`
  margin-top: 60px;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: ${COLORS.darkGray};
  margin-bottom: 30px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const IntroText = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: ${COLORS.textGray};
  margin-bottom: 40px;
  text-align: left;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const MeasurementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MeasurementItem = styled.li`
  margin-bottom: 50px;
  padding-bottom: 50px;
  border-bottom: 2px solid ${COLORS.lightGray};

  &:last-child {
    border-bottom: none;
  }
`;

const MeasurementTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${COLORS.darkGray};
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const MeasurementDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: ${COLORS.textGray};
  margin-bottom: 25px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ImageGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const InstructionImage = styled.img`
  max-width: 350px;
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  cursor: zoom-in;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ImageCaption = styled.div`
  text-align: center;
  margin-top: 12px;
  font-size: 16px;
  color: ${COLORS.textGray};
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 280px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const MeasureFilterPage = () => {
  const [zoomState, setZoomState] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  // Images are in the public/assets folder
  const measurementImages = [
    '/assets/id-right_s.webp',
    '/assets/id-wrong_s.webp',
    '/assets/od-t_s.webp',
    '/assets/od-b-right_s.webp',
    '/assets/od-b-wrong_s.webp',
    '/assets/h-right_s.webp',
    '/assets/h-wrong_s.webp',
  ];

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setZoomState(true);
  };

  const handleZoomClose = (state: boolean) => {
    setZoomState(state);
  };

  return (
    <Container>
      <Header />
      <div className="header-gap-creator"></div>

      <main>
      <Breadcrumbs items={[{ label: 'How to Measure a Filter' }]} />
      <HeroSection>
        <HeroTitle>How to Measure an Air Filter</HeroTitle>
      </HeroSection>

      <ContentSection>
        <VideoSection>
          <VideoTitle>Step-by-Step Air Filter Measurement Guide</VideoTitle>
          <VideoCard>
            <VideoWrapper>
              <iframe
                src="https://www.youtube.com/embed/Gz90LhIE08E"
                title="Green Filter USA - Measuring and Finding a Clamp-On Filter"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </VideoWrapper>
          </VideoCard>
        </VideoSection>

        <InstructionsSection>
          <SectionTitle>Complete Air Filter Size Guide and Measurement Instructions</SectionTitle>
          <IntroText>
            Learning how to measure air filter dimensions accurately is essential for finding the perfect
            fit for your vehicle. Whether you need to measure a round air filter or determine air filter
            dimensions by size, this comprehensive air filter fitment guide will walk you through each step.
            Use our air filter size chart and measurement techniques below to find the correct air filter
            by size for your custom installation or replacement needs.
          </IntroText>

          <IntroText>
            Understanding air filter measurements ensures proper fitment and optimal engine performance.
            Follow our detailed air filter size guide to measure inside diameter, outside diameter, base
            diameter, and height with precision. These air filter dimensions are critical for selecting
            the right Green High Performance Air Filter for your application.
          </IntroText>

          <MeasurementList>
            <MeasurementItem>
              <MeasurementTitle>How to Measure Air Filter Inside Diameter (ID)</MeasurementTitle>
              <MeasurementDescription>
                When measuring air filter dimensions, start with the inside diameter. This critical air filter
                measurement determines the proper fitment on your intake tube. Measure across the inside opening
                of the filter opening, NOT from the outsides of the flange, to get accurate air filter dimensions.
              </MeasurementDescription>
              <ImageGrid>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[0]}
                    alt="Correct way to measure inside diameter"
                    onClick={() => handleImageClick(0)}
                  />
                  <ImageCaption>✓ Correct</ImageCaption>
                </ImageWrapper>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[1]}
                    alt="Incorrect way to measure inside diameter"
                    onClick={() => handleImageClick(1)}
                  />
                  <ImageCaption>✗ Incorrect</ImageCaption>
                </ImageWrapper>
              </ImageGrid>
            </MeasurementItem>

            <MeasurementItem>
              <MeasurementTitle>Round Air Filter Measurements: Outside Diameter - Top (OD-T)</MeasurementTitle>
              <MeasurementDescription>
                For round air filter sizes, the top outside diameter is a key air filter dimension. Measure
                across the widest part of the top of the filter from the outside edges. This round air filter
                measurement helps ensure proper clearance in your engine bay and accurate air filter fitment.
              </MeasurementDescription>
              <ImageGrid>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[2]}
                    alt="How to measure outside diameter - top"
                    onClick={() => handleImageClick(2)}
                  />
                </ImageWrapper>
              </ImageGrid>
            </MeasurementItem>

            <MeasurementItem>
              <MeasurementTitle>Air Filter Dimensions: Outside Diameter - Base (OD-B)</MeasurementTitle>
              <MeasurementDescription>
                The base outside diameter is another essential air filter measurement for determining the correct
                air filter by size. Measure across the base of the filter. Since the filter&apos;s flange will
                likely be in the way, you will need to measure across the top of the flange to get accurate air
                filter dimensions. This measurement is critical for proper air filter fitment.
              </MeasurementDescription>
              <ImageGrid>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[3]}
                    alt="Correct way to measure outside diameter - base"
                    onClick={() => handleImageClick(3)}
                  />
                  <ImageCaption>✓ Correct</ImageCaption>
                </ImageWrapper>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[4]}
                    alt="Incorrect way to measure outside diameter - base"
                    onClick={() => handleImageClick(4)}
                  />
                  <ImageCaption>✗ Incorrect</ImageCaption>
                </ImageWrapper>
              </ImageGrid>
            </MeasurementItem>

            <MeasurementItem>
              <MeasurementTitle>Air Filter Size Guide: Measuring Height (H)</MeasurementTitle>
              <MeasurementDescription>
                Height is the final critical air filter measurement needed to find the correct air filter by size.
                Measure from the base of the filter (not the flange) to the top of the filter, including the
                filter end cap. Accurate height measurements combined with diameter measurements ensure you select
                the right air filter by dimensions for optimal performance and proper air filter fitment.
              </MeasurementDescription>
              <ImageGrid>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[5]}
                    alt="Correct way to measure height"
                    onClick={() => handleImageClick(5)}
                  />
                  <ImageCaption>✓ Correct</ImageCaption>
                </ImageWrapper>
                <ImageWrapper>
                  <InstructionImage
                    src={measurementImages[6]}
                    alt="Incorrect way to measure height"
                    onClick={() => handleImageClick(6)}
                  />
                  <ImageCaption>✗ Incorrect</ImageCaption>
                </ImageWrapper>
              </ImageGrid>
            </MeasurementItem>
          </MeasurementList>

          <div style={{ marginTop: '60px' }}>
            <SectionTitle>Air Filter Size Chart and Fitment Guide</SectionTitle>
            <IntroText>
              Once you've learned how to measure an air filter using the steps above, you can use our comprehensive
              air filter size chart to find the perfect match. Green Filter offers hundreds of round air filter sizes
              and configurations to fit virtually any application. Search for air filters by dimensions using our
              online catalog, or contact our team for assistance with custom air filter measurements.
            </IntroText>

            <IntroText>
              Understanding air filter dimensions and how to measure air filter components correctly ensures optimal
              engine performance and proper fitment. Whether you're looking for a specific air filter by size or need
              help interpreting round air filter measurements, our air filter fitment guide and size chart make it
              easy to find exactly what you need. All Green Filter products are made in the USA and backed by our
              lifetime warranty.
            </IntroText>

            <IntroText>
              <strong>Need help finding the right air filter by dimensions?</strong> Use our{' '}
              <a href="/shop-by-vehicle" style={{ color: COLORS.brandGreen, textDecoration: 'none', fontWeight: 600 }}>
                vehicle selector tool
              </a>{' '}
              to search by make and model, or browse our complete{' '}
              <a href="/cross-reference-filters" style={{ color: COLORS.brandGreen, textDecoration: 'none', fontWeight: 600 }}>
                air filter cross reference
              </a>{' '}
              to match OEM part numbers. Our experts are ready to help you select the correct air filter using your
              air filter measurements.
            </IntroText>
          </div>
        </InstructionsSection>
      </ContentSection>

      <FAQSection faqs={measureFilterFaqs} title="FAQs About Measuring an Air Filter" />

      <ZoomBox
        dataArr={measurementImages}
        imageIndex={selectedImageIndex}
        setImageIndex={setSelectedImageIndex}
        state={zoomState}
        event={handleZoomClose}
        zoom="2"
      />
      </main>

      <Footer />
    </Container>
  );
};

export default MeasureFilterPage;
